import * as Immutable from 'immutable';

import { INITIAL_STATE_HYDRATION_OMIT, INITIAL_STATE_POJO_KEYS } from './initialStateConstants';
import { produceImmutableReducerState } from '../offline/cache/utils/rebuildPersistedState';
import { getHasLocalAuthHint } from '../auth/authService';
import { GUEST_CURRENT_USER_STATE } from '../user/currentUserReducer';

const { requestStateSync } = window.desktopIntegration;

const prepareState = (state: Record<string, unknown>) => {
    const immutableState = produceImmutableReducerState(state, INITIAL_STATE_POJO_KEYS, INITIAL_STATE_HYDRATION_OMIT);

    // The synced state carries whatever the current user was when the app last closed, so derive the
    // starting point fresh rather than trusting it. This matches the web reducer's initial state
    // exactly — `isGuest` is hardcoded to `false` no longer: that was only needed while
    // `getCurrentUserToken()` was the sole hint, and it would have reported a migrated Better Auth
    // user (whose legacy token has been discarded) as a guest. The remembered-session hint covers
    // that case now.
    //
    // Replace the whole map rather than merging over it: `app.currentUser` is rehydrated whole, so a
    // state synced from a logged-in session carries that user's profile alongside `authenticated`
    // and `fetched` as `true`. Consumers read those rather than `isGuest` — `AuthenticatedContainer`
    // gates on `authenticated && fetched` — and merging a few flags would strand the rest of the
    // profile in a state that claims to be a guest.
    if (!getHasLocalAuthHint()) {
        return immutableState.setIn(['app', 'currentUser'], Immutable.Map(GUEST_CURRENT_USER_STATE));
    }

    // With a hint there is a session worth restoring, so the rest of the synced current user stands
    // until `fetchCurrentUser` confirms or clears it.
    return immutableState.setIn(['app', 'currentUser', 'isGuest'], false);
};

const getElectronInitialState = () => {
    const state = requestStateSync();
    if (!state) return;

    const preparedState = prepareState(state);
    return preparedState;
};

export default getElectronInitialState;
